import { useState, useRef, useEffect, useCallback } from "react";
import { Navigate, Link, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { AppLayout } from "@/components/AppLayout";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Send, Trash2, Zap, Lock, ShieldOff, HelpCircle } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { useQuery } from "@tanstack/react-query";
import { apiSendAssistantMessage, apiGetTenantConfig, apiGetNavPermissions, getTenantSlug, type AssistantMessage as ApiMessage } from "@/lib/api";
import {
  AssistantMessage as MessageBubble,
  TypingIndicator,
  type Message,
  type UserAvatarInfo,
} from "@/components/ai/AssistantMessage";

// Quick suggestion chips shown in empty state
const SUGGESTIONS = [
  { label: "Current headcount",      prompt: "What is our current total headcount and breakdown by department?" },
  { label: "Payroll summary",        prompt: "Show me the payroll summary for the last 6 months — total gross and net costs." },
  { label: "List active employees",  prompt: "List all active employees." },
  { label: "HR analytics overview",  prompt: "Give me a quick HR overview: headcount, new hires this month, and turnover trend." },
  { label: "Department comparison",  prompt: "Compare all departments by headcount and average tenure." },
];

// Command palette shortcuts shown when user types "/"
const COMMANDS = [
  { command: "/onboarding", hint: "Start employee onboarding" },
  { command: "/survey",     hint: "Create a survey" },
  { command: "/report",     hint: "Generate an analytics report" },
  { command: "/workflow",   hint: "Create a workflow automation" },
];

function expandCommand(text: string): string {
  const trimmed = text.trim();
  if (trimmed === "/onboarding")
    return "I want to create an onboarding session for an employee. Can you first show me the available templates and list some employees I can choose from?";
  if (trimmed === "/survey")
    return "I want to create a new employee survey. What kind of survey should it be? (e.g. satisfaction, onboarding feedback, pulse check)";
  if (trimmed === "/report")
    return "I want to generate an HR report. What data do you need: headcount, payroll, leave trends, turnover, or department comparison?";
  if (trimmed === "/workflow")
    return "I want to create a new workflow automation. What HR process should it handle? (e.g. salary increase approval, leave approval, onboarding)";
  return text;
}

export default function AIAssistant() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();

  const { data: tenantConfig } = useQuery({
    queryKey: ["tenant-config"],
    queryFn: apiGetTenantConfig,
    staleTime: 10 * 60 * 1000,
  });

  const { data: navPermissions } = useQuery({
    queryKey: ["nav-permissions"],
    queryFn: apiGetNavPermissions,
    staleTime: 5 * 60 * 1000,
  });

  // ── All hooks must be declared before any early returns ──────────────────
  // Stable storage key scoped to tenant + user so separate users don't share history
  const storageKey = `besthr_${getTenantSlug()}_${user?.id ?? "anon"}_sara`;

  const [sessionId] = useState<string>(() => {
    try {
      const stored = localStorage.getItem(`${storageKey}_sid`);
      if (stored) return stored;
    } catch {}
    const id = typeof crypto !== "undefined" && typeof crypto.randomUUID === "function"
      ? crypto.randomUUID()
      : `${Date.now()}-${Math.random().toString(36).slice(2)}`;
    try { localStorage.setItem(`${storageKey}_sid`, id); } catch {}
    return id;
  });

  const [messages, setMessages] = useState<Message[]>(() => {
    try {
      const raw = localStorage.getItem(`${storageKey}_msgs`);
      if (raw) return JSON.parse(raw) as Message[];
    } catch {}
    return [];
  });
  const [input, setInput]               = useState("");
  const [isLoading, setIsLoading]       = useState(false);
  const [showCommands, setShowCommands] = useState(false);

  const bottomRef   = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const scrollRef   = useRef<HTMLDivElement>(null);

  // Pre-fill input from ?prompt= query param (e.g. coming from Sara Help page)
  // If ?autoSend=1 is also present, auto-send immediately without any user interaction.
  // setTimeout(0) defers the call until after all hooks have initialised for this render.
  useEffect(() => {
    const promptParam = searchParams.get("prompt");
    const autoSend    = searchParams.get("autoSend") === "1";
    if (promptParam) {
      setSearchParams({}, { replace: true });
      if (autoSend) {
        // Closure captures sendMessage binding; by the time setTimeout fires
        // (after the current call-stack / paint), sendMessage is fully initialised.
        setTimeout(() => sendMessage(promptParam), 0);
      } else {
        setInput(promptParam);
        setTimeout(() => textareaRef.current?.focus(), 100);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, isLoading]);

  // Persist messages to localStorage on every change
  useEffect(() => {
    try {
      localStorage.setItem(`${storageKey}_msgs`, JSON.stringify(messages));
    } catch {}
  }, [messages, storageKey]);

  useEffect(() => {
    const trimmed = input.trimStart();
    setShowCommands(trimmed.startsWith("/") && !trimmed.includes(" ") && trimmed.length <= 20);
  }, [input]);

  const sendMessage = useCallback(
    async (text: string) => {
      const trimmed = text.trim();
      if (!trimmed || isLoading) return;

      const finalPrompt = expandCommand(trimmed);
      const userMsg: Message = {
        id:        `${Date.now()}-user`,
        role:      "user",
        content:   finalPrompt,
        timestamp: new Date(),
      };

      setMessages((prev) => [...prev, userMsg]);
      setInput("");
      setIsLoading(true);
      setShowCommands(false);

      const apiMessages: ApiMessage[] = [...messages, userMsg].map((m) => ({
        role:    m.role,
        content: m.content,
      }));

      try {
        const response = await apiSendAssistantMessage(apiMessages, sessionId);
        setMessages((prev) => [...prev, {
          id:           `${Date.now()}-assistant`,
          role:         "assistant",
          content:      response.message,
          actionsTaken: response.actionsTaken,
          timestamp:    new Date(),
        }]);
      } catch (err: any) {
        setMessages((prev) => [...prev, {
          id:        `${Date.now()}-error`,
          role:      "assistant",
          content:   err?.message?.toLowerCase().includes("not configured")
            ? "AI features are not configured for this platform. Please contact your administrator to set up the OpenAI API key."
            : "Sorry, something went wrong. Please try again.",
          timestamp: new Date(),
          isError:   true,
        }]);
      } finally {
        setIsLoading(false);
        setTimeout(() => textareaRef.current?.focus(), 50);
      }
    },
    [messages, isLoading, sessionId],
  );
  // ─────────────────────────────────────────────────────────────────────────

  if (!user) return <Navigate to="/login" replace />;

  const userAvatar: UserAvatarInfo = {
    avatarUrl:   user.avatarUrl,
    avatarColor: user.avatarColor ?? "from-purple-500 to-indigo-500",
    initials:    user.initials ?? "?",
  };

  // Module disabled at the plan/tenant level (superadmin toggled it off) → contact sales
  const aiModuleOff = tenantConfig?.enabledModules != null &&
    tenantConfig.enabledModules["aiAssistant"] === false;

  // Module is available at tenant level but admin disabled it for this role → contact admin
  const aiNavSetting = navPermissions?.find((p) => p.navKey === "aiAssistant");
  const roleKey = user?.role?.mainRole ?? "user";
  const roleMap: Record<string, keyof NonNullable<typeof aiNavSetting>> = {
    admin:         "visibleToAdmin",
    hr:            "visibleToHr",
    user:          "visibleToUser",
    line_manager:  "visibleToLineManager",
    c_level:       "visibleToCLevel",
  };
  const navField = roleMap[roleKey] ?? "visibleToUser";
  const aiRoleBlocked = !aiModuleOff && aiNavSetting != null && aiNavSetting[navField] === false;

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); sendMessage(input); }
    if (e.key === "Escape") setShowCommands(false);
  };

  const handleInput = (e: React.FormEvent<HTMLTextAreaElement>) => {
    const el = e.currentTarget;
    el.style.height = "auto";
    el.style.height = `${Math.min(el.scrollHeight, 128)}px`;
  };

  const clearConversation = () => {
    setMessages([]);
    setInput("");
    try {
      localStorage.removeItem(`${storageKey}_msgs`);
      localStorage.removeItem(`${storageKey}_sid`);
    } catch {}
    setTimeout(() => textareaRef.current?.focus(), 50);
  };

  // ── Module disabled at plan level — contact sales ────────────────────────
  if (aiModuleOff) {
    return (
      <AppLayout>
        <div className="flex h-[calc(100vh-8rem)] items-center justify-center">
          <div className="flex flex-col items-center text-center max-w-sm gap-5">
            <div className="flex h-16 w-16 items-center justify-center rounded-2xl bg-muted">
              <Lock className="h-8 w-8 text-muted-foreground" />
            </div>
            <div>
              <h2 className="text-xl font-semibold mb-2">Sara AI Assistant</h2>
              <p className="text-sm text-muted-foreground">
                The AI Assistant is not included in your current plan.
              </p>
              <p className="text-sm text-muted-foreground mt-1">
                To unlock Sara and supercharge your HR workflows,{" "}
                <a
                  href="mailto:sales@besthr.app"
                  className="text-primary font-medium hover:underline"
                >
                  contact our sales team
                </a>
                .
              </p>
            </div>
          </div>
        </div>
      </AppLayout>
    );
  }

  // ── Access restricted by admin for this role ──────────────────────────────
  if (aiRoleBlocked) {
    return (
      <AppLayout>
        <div className="flex h-[calc(100vh-8rem)] items-center justify-center">
          <div className="flex flex-col items-center text-center max-w-sm gap-5">
            <div className="flex h-16 w-16 items-center justify-center rounded-2xl bg-muted">
              <ShieldOff className="h-8 w-8 text-muted-foreground" />
            </div>
            <div>
              <h2 className="text-xl font-semibold mb-2">Sara AI Assistant</h2>
              <p className="text-sm text-muted-foreground">
                Access to the AI Assistant has been restricted for your role.
              </p>
              <p className="text-sm text-muted-foreground mt-1">
                Please contact your administrator to request access.
              </p>
            </div>
          </div>
        </div>
      </AppLayout>
    );
  }

  return (
    <AppLayout>
      <div className="flex h-[calc(100vh-4rem)] flex-col overflow-hidden">
        {/* ── Header ────────────────────────────────────────────────────────── */}
        <div className="flex flex-shrink-0 items-center justify-between border-b px-6 py-4">
          <div className="flex items-center gap-3">
            <div className="flex h-9 w-9 items-center justify-center rounded-xl overflow-hidden bg-white shadow-md border border-border">
              <img src="/besthr.png" alt="Sara" className="h-7 w-7 object-contain" />
            </div>
            <div>
              <h1 className="text-lg font-semibold">Sara AI Assistant</h1>
              <p className="text-xs text-muted-foreground">BestHR.app Intelligent Companion</p>
            </div>
          </div>

          <div className="flex items-center gap-2">
            {messages.length > 0 && (
              <Badge variant="secondary" className="text-xs">
                {messages.length} message{messages.length !== 1 ? "s" : ""}
              </Badge>
            )}
            <Button
              variant="ghost"
              size="sm"
              asChild
              className="gap-1.5 text-muted-foreground hover:text-foreground"
            >
              <Link to="/sara-help">
                <HelpCircle className="h-3.5 w-3.5" />
                What can Sara do?
              </Link>
            </Button>
            {messages.length > 0 && (
              <Button
                variant="ghost"
                size="sm"
                onClick={clearConversation}
                className="gap-1.5 text-muted-foreground hover:text-foreground"
              >
                <Trash2 className="h-3.5 w-3.5" />
                {t("aiAssistant.clear")}
              </Button>
            )}
          </div>
        </div>

        {/* ── Messages ──────────────────────────────────────────────────────── */}
        <ScrollArea className="flex-1" ref={scrollRef as any}>
          <div className="space-y-6 px-6 py-6">
            {messages.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-10 text-center">
                <div className="mb-4 flex h-16 w-16 items-center justify-center rounded-2xl overflow-hidden bg-white shadow-lg border border-border">
                  <img src="/besthr.png" alt="Sara" className="h-12 w-12 object-contain" />
                </div>
                <h2 className="mb-1 text-xl font-semibold">Hi, I'm Sara!</h2>
                <p className="mb-8 max-w-md text-sm text-muted-foreground">
                  BestHR.app Intelligent Companion — ask me anything about your HR data or trigger an action.
                </p>
                <div className="mb-8 flex flex-wrap justify-center gap-2">
                  {SUGGESTIONS.map((s) => (
                    <button
                      key={s.label}
                      onClick={() => sendMessage(s.prompt)}
                      disabled={isLoading}
                      className="rounded-full border bg-card px-4 py-2 text-sm transition-colors hover:border-primary/30 hover:bg-accent disabled:opacity-50"
                    >
                      {s.label}
                    </button>
                  ))}
                </div>
                <div className="flex items-center gap-3 text-xs text-muted-foreground flex-wrap justify-center">
                  <span className="flex items-center gap-1.5">
                    <Zap className="h-3 w-3" />
                    {t("aiAssistant.commandPalette")}
                  </span>
                  <span className="text-border">|</span>
                  <Link to="/sara-help" className="flex items-center gap-1.5 hover:text-violet-600 transition-colors">
                    <HelpCircle className="h-3 w-3" />
                    See everything Sara can do
                  </Link>
                </div>
              </div>
            ) : (
              messages.map((msg) => <MessageBubble key={msg.id} message={msg} userAvatar={userAvatar} />)
            )}
            {isLoading && <TypingIndicator />}
            <div ref={bottomRef} />
          </div>
        </ScrollArea>

        {/* ── Input ─────────────────────────────────────────────────────────── */}
        <div className="flex-shrink-0 border-t px-6 py-4">
          {showCommands && (
            <div className="mb-3 overflow-hidden rounded-xl border bg-card shadow-lg">
              <div className="border-b px-3 py-2 text-xs font-semibold text-muted-foreground">
                Quick commands
              </div>
              {COMMANDS.map((cmd) => (
                <button
                  key={cmd.command}
                  onClick={() => {
                    setInput(cmd.command + " ");
                    setShowCommands(false);
                    textareaRef.current?.focus();
                  }}
                  className="flex w-full items-center gap-3 px-3 py-2 text-left text-sm transition-colors hover:bg-accent"
                >
                  <span className="font-mono text-primary">{cmd.command}</span>
                  <span className="text-muted-foreground">{cmd.hint}</span>
                </button>
              ))}
            </div>
          )}
          <div className="flex items-end gap-3">
            <div className="relative flex-1">
              <Textarea
                ref={textareaRef}
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={handleKeyDown}
                onInput={handleInput}
                placeholder={t("aiAssistant.placeholder")}
                disabled={isLoading}
                rows={1}
                className="min-h-[44px] max-h-32 resize-none py-2.5 pr-4 text-sm"
                style={{ height: "44px" }}
              />
            </div>
            <Button
              onClick={() => sendMessage(input)}
              disabled={!input.trim() || isLoading}
              size="icon"
              className="h-[44px] w-[44px] flex-shrink-0 bg-gradient-to-br from-violet-500 to-indigo-600 shadow-md hover:from-violet-600 hover:to-indigo-700"
            >
              <Send className="h-4 w-4" />
            </Button>
          </div>
          <p className="mt-2 text-center text-[10px] text-muted-foreground">
            Press Enter to send · Shift+Enter for new line · Type / for commands
          </p>
        </div>
      </div>
    </AppLayout>
  );
}
